import { CacheService } from '../../common/services/CacheService';
import { GQLIdEncoder, ModelType } from '../../common/utils/GQLIdEncoder';
import { ProjectMapper } from '../mappers/ProjectMapper';
import { ReportMapper } from '../mappers/ReportMapper';
import { Project } from '../models/Project';
import { Report } from '../models/Report';

/** TODO(CGFT-425): Delete this service and references to it once we are fully migrated to SPA */

/** Generic type for a ModelMapper mapping function e.g. SlotBreadcrumbMapper.mapGQLModel  */
type MapperFunction<Model> = (data: any) => Model;

const BREADCRUMB_PREFIX = 'breadcrumb.';

export class BreadcrumbCachingService {
  private readonly cacheService = CacheService.getInstance();
  private readonly gqlIdEncoder = new GQLIdEncoder();
  private readonly projectBreadcrumbMapper = new ProjectMapper();
  private readonly reportBreadcrumbMapper = new ReportMapper();

  /**
   * Retrieves the breadcrumb for a project
   * @param id the ID of the project to retrieve
   */
  getProject(id: number) {
    return this.getCachedModel<Project>(ModelType.Project, (data) => this.projectBreadcrumbMapper.mapGQLModel(data), id);
  }

  /**
   * Saves the breadcrumb for a project
   * @param model the breadcrumb to save
   */
  saveProject(model: Project) {
    const rawData = model.toRawJson();

    this.setCachedModel(rawData.id, rawData);
  }

  /**
   * Removes all breadcrumbs related to a project from the cache
   * @param id the ID of the project to clear
   */
  clearProject(id: number) {
    const b64Id = this.gqlIdEncoder.encode(ModelType.Project, id);
    this.clearCachedModel(b64Id);
    this.clearByPredicate((value) => 'slot' in value && 'project' in value.slot && 'id' in value.slot.project && value.slot.project.id === b64Id);
  }

  /**
   * Retrieves the breadcrumb for a report
   * @param id the ID of the report to retrieve
   */
  getReport(id: number) {
    return this.getCachedModel<Report>(ModelType.Report, (data) => this.reportBreadcrumbMapper.mapGQLModel(data), id);
  }

  /**
   * Saves the breadcrumb for a report
   * @param model the breadcrumb to save
   * @param  timeToLive optional TTL override
   */
  saveReport(model: Report, timeToLive?: number) {
    const rawData = model.toRawJson();

    this.setCachedModel(rawData.id, rawData, timeToLive);
  }

  /**
   * Removes all breadcrumbs related to a slot from the cache
   * @param id the ID of the slot to clear
   */
  clearSlot(id: number) {
    const b64Id = this.gqlIdEncoder.encode(ModelType.Slot, id);
    this.clearByPredicate((value) => 'slot' in value && 'id' in value.slot && value.slot.id === b64Id);
  }

  /**
   * Formats model's nodeId to a cacheKey in the following format "breadcrumb.{modelNodeId}".
   * @param id the encoded model ID
   */
  private getCacheId(id: string): string {
    return `${BREADCRUMB_PREFIX}${id}`;
  }

  /**
   * Generic model caching handler
   * @param type the model type name
   * @param mapperFunction function to convert the cached value to the expected model
   * @param id the ID of the model to cache
   */
  private getCachedModel<Model>(type: ModelType, mapperFunction: MapperFunction<Model>, id: number) {
    try {
      if (id === null) throw null;

      const data = this.cacheService.getItem(this.getCacheId(this.gqlIdEncoder.encode(type, id)));

      if (!data) return null;

      return mapperFunction(data);
    } catch {
      return null;
    }
  }

  /**
   * Generic breadcrumb cache setter for uniform cache keys.
   * @param id the encoded cache ID
   * @param rawJson raw json data to store
   * @param timeToLive optional TTL override
   */
  private setCachedModel(id: string, rawJson: any, timeToLive?: number) {
    this.cacheService.setItem(this.getCacheId(id), rawJson, timeToLive);
  }

  /**
   * Removes a breadcrumb from cache
   * @param id the encoded resource ID
   */
  private clearCachedModel(id: string) {
    this.cacheService.clearItem(this.getCacheId(id));
  }

  /**
   * Removes all breadcrumbs that match the predicate from cache
   * @param predicate function to evaluate on each cached breadcrumb
   */
  private clearByPredicate(predicate: (value: any) => boolean) {
    for (const key in localStorage) {
      if (!key.startsWith(BREADCRUMB_PREFIX)) continue;

      const value = this.cacheService.getItem<any>(key);
      if (!value) continue;

      if (predicate(value)) {
        this.cacheService.clearItem(key);
      }
    }
  }
}
